import React, { useEffect, useRef, useState } from "react";
import api from "../../lib/api";
import { useCurrency } from "../../lib/contexts";
import { Button } from "../../components/ui/button";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import { toast } from "sonner";

export default function LostReasonDialog({ prompt, onClose, onSaved }) {
  const MIN_CHARS = 50;
  const [reason, setReason] = useState("");
  const [saving, setSaving] = useState(false);
  const { format } = useCurrency();
  const textareaRef = useRef(null);
  const isEditMode = !!prompt?.edit;
  useEffect(() => {
    if (prompt?.deal) {
      setReason(prompt.deal.lost_reason || "");
      // Autofocus the textarea so the input is obvious and immediately usable.
      setTimeout(() => textareaRef.current?.focus(), 60);
    }
  }, [prompt]);
  if (!prompt) return null;
  const deal = prompt.deal;
  const trimmed = reason.trim();
  const remaining = Math.max(0, MIN_CHARS - trimmed.length);
  const canSubmit = trimmed.length >= MIN_CHARS;
  const submit = async (e) => {
    e.preventDefault();
    if (!canSubmit) {
      toast.error(`Please add at least ${MIN_CHARS} characters (${remaining} more needed)`);
      textareaRef.current?.focus();
      return;
    }
    setSaving(true);
    try {
      if (isEditMode) {
        await api.patch(`/deals/${deal.id}/lost-reason`, { lost_reason: trimmed });
        toast.success("Lost Reason updated");
      } else {
        await api.patch(`/deals/${deal.id}/stage`, { stage: "Lost", lost_reason: trimmed });
        toast.success("Deal marked as Lost");
      }
      onSaved();
      onClose();
    } catch (err) { toast.error(err.response?.data?.detail || "Save failed"); }
    setSaving(false);
  };
  return (
    <Dialog open={!!prompt} onOpenChange={onClose}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="font-heading text-destructive">
            {isEditMode ? "Update Lost Reason" : "Mark Deal as Lost"}
          </DialogTitle>
        </DialogHeader>

        {/* Deal summary so the user sees which deal they are closing */}
        <div className="border border-border rounded-md bg-secondary/40 p-3 space-y-1 text-sm">
          <div className="flex items-center justify-between gap-2">
            <span className="font-semibold">{deal.product_name || "—"}</span>
            {deal.product_code && (
              <span className="text-xs font-mono bg-neutral-200 px-1.5 py-0.5 rounded">{deal.product_code}</span>
            )}
          </div>
          <div className="text-muted-foreground">{deal.contact_name}</div>
          <div className="flex items-center gap-4 text-xs text-muted-foreground pt-1 flex-wrap">
            <span>Owner: <span className="text-neutral-800 font-medium">{deal.created_by_name || "—"}</span></span>
            <span>Estimated: <span className="text-neutral-800 font-medium">{format(deal.estimated_value)}</span></span>
            {isEditMode
              ? <span className="text-destructive font-semibold">Stage: Lost (updating remark)</span>
              : <span>From: <span className="text-neutral-800 font-medium">{deal.stage}</span> → <span className="text-destructive font-semibold">Lost</span></span>}
          </div>
        </div>

        <form onSubmit={submit} className="space-y-3 mt-3">
          <div>
            <Label htmlFor="lost-reason-input" className="text-sm font-semibold text-neutral-900">
              Remark — why did we lose this deal? <span className="text-destructive">*</span>
            </Label>
            <p className="text-[11px] text-muted-foreground mt-0.5 mb-2">
              {isEditMode
                ? "Add more detail now that you have more context. Everyone on the team sees this — keep it factual."
                : `Type your reason below. Minimum ${MIN_CHARS} characters — please add real context so the team can learn from it.`}
            </p>
            <Textarea id="lost-reason-input" ref={textareaRef} data-testid="lost-reason" rows={6}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="Type your reason here… e.g. Client chose competitor X due to 8% lower price and faster lead-time; our lecithin grade did not match their formulation spec on peroxide value."
              className="border-2 border-destructive/40 focus-visible:ring-destructive/40 focus-visible:border-destructive bg-white text-base min-h-[140px]" />
            <div className={`text-[11px] mt-1 text-right ${canSubmit ? "text-emerald-700" : "text-destructive"}`}>
              {canSubmit
                ? `✓ ${trimmed.length} character(s) — good to save`
                : `${trimmed.length} / ${MIN_CHARS} minimum — ${remaining} more character(s) needed`}
            </div>
          </div>
          <div className="flex justify-end gap-2 border-t border-border pt-3">
            <Button type="button" variant="ghost" onClick={onClose}>Cancel</Button>
            <Button data-testid="lost-submit" type="submit" disabled={saving || !canSubmit}
              className="bg-destructive hover:bg-destructive/90 text-white disabled:opacity-50">
              {saving ? "Saving…" : (isEditMode ? "Save Changes" : "Save & Mark as Lost")}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
